/**
 * "Alert history" on the Alerts page (help `cloud.alerts`; docs/specs/cloud/alerts.md §5; Diurnal parity C1, and C3
 * for 02-015): the organisation's stored alerts of the last 7, 30 or 90 days, "Needs action" (not resolved),
 * "Acknowledged" (seen and not resolved, R141), "All" or "Resolved"; what was sent; Acknowledge, Snooze (1, 4 or 12
 * hours: no notice of the same kind about the same station or aircraft on the same project meanwhile; never an SOS),
 * Resolve with a note (never for the acknowledge-only kinds, which acknowledging finishes: Flight started and Flight
 * ended R140, Tracking resumed decision 941, Magnetic exceedances MB 3 Nov 2026; `canResolveNotice`), Reopen, for
 * field crew, managers, data processors and tenant admins (decision 736); and the
 * history as CSV. A notice or email opens it at its alert.
 * At the top of the page (R122, MB 27 Oct 2026: "so staff are reminded to acknowledge alerts"): the alerts not yet
 * acknowledged first (`noticeHistoryOrder`), a line saying how many wait, and "Acknowledge" beside each one's title.
 * After an action the list keeps its order, so the next alert never moves under a second tap; the first 10 show,
 * with "Show N more" for the rest.
 * "Acknowledge all" (decision 835, Q746 C) at the top: every waiting alert of the period except SOS and lost
 * tracking, which keep their own "Acknowledge" (the rule is the domain's `noticesToAcknowledgeAll`); it says how many
 * and asks first. Only the people who acknowledge see it.
 * "Remove" (R159, tenant admins only, `canRemoveNotices`): takes an alert out of the history for everyone after
 * asking; an SOS or lost-tracking alert only once acknowledged (`noticeRemoveProblem`). Hidden in the database, never
 * deleted, so the poll that raised it does not raise it again; audited.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  NOTICE_SNOOZE_CHOICES,
  NOTICE_STATE_LABEL,
  canAcknowledgeNotice,
  canRemoveNotices,
  canResolveNotice,
  canSnoozeNotice,
  noticeHistoryCsv,
  noticeHistoryOrder,
  noticeInFilter,
  noticeNeedsAcknowledging,
  noticeRemoveProblem,
  noticeResolvedByTeslyn,
  noticeState,
  noticesToAcknowledgeAll,
  acknowledgeAllQuestion,
  removeNoticeQuestion,
  type NoticeHistoryFilter,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import {
  acknowledge,
  acknowledgeAll,
  loadRecent,
  removeAlert,
  reopen,
  resolve,
  snooze,
  type RecentAlert,
} from './api.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const when = (iso: string): string =>
  new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const STATUS_WORDS: Record<RecentAlert['status'], (n: number) => string> = {
  pending: () => 'Sending…',
  sent: (n) => `Sent to ${n} ${n === 1 ? 'person' : 'people'}`,
  failed: () => 'Not delivered: every notice failed',
  none: () => 'Nobody to send to',
  snoozed: () => 'Not sent: snoozed',
};

const FILTERS: readonly [NoticeHistoryFilter, string][] = [
  ['needs_action', 'Needs action'],
  ['acknowledged', 'Acknowledged'],
  ['all', 'All'],
  ['resolved', 'Resolved'],
];

/** What an empty list says, when the period has alerts but the filter leaves none. */
const EMPTY_WORDS: Readonly<Record<NoticeHistoryFilter, (days: number) => string>> = {
  needs_action: () => 'Nothing here: every alert is resolved or acknowledged.',
  acknowledged: (days) => `No acknowledged alerts in the last ${days} days.`,
  resolved: (days) => `No resolved alerts in the last ${days} days.`,
  all: (days) => `No alerts in the last ${days} days.`,
};

/** The rows that wait to be acknowledged, and "Acknowledge all", show under these filters only. */
const WAITING_SHOWN: readonly NoticeHistoryFilter[] = ['needs_action', 'all'];

/** The alerts shown before "Show N more". */
const FIRST = 10;

const fields = (a: RecentAlert) => ({
  kind: a.kind,
  acknowledged_at: a.acknowledged_at,
  snoozed_until: a.snoozed_until ?? null,
  resolved_at: a.resolved_at ?? null,
});

/** Offer text as a download. */
function browserSave(fileName: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export interface AlertHistoryProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly userId: string;
  readonly roles: readonly string[];
  readonly names: ReadonlyMap<string, string>;
  readonly openAlertId?: string | null;
  readonly now?: () => number;
  readonly save?: (fileName: string, text: string) => void;
}

export function AlertHistory({
  sb,
  tenantId,
  userId,
  roles,
  names,
  openAlertId = null,
  now = () => Date.now(),
  save = browserSave,
}: AlertHistoryProps): ReactElement {
  const [days, setDays] = useState(7);
  const [filter, setFilter] = useState<NoticeHistoryFilter>('needs_action');
  const [rows, setRows] = useState<RecentAlert[] | null>(null);
  // the ids in the order shown: set when the list is read afresh, kept after an action (new alerts go first)
  const [order, setOrder] = useState<readonly string[]>([]);
  const [all, setAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [resolving, setResolving] = useState<string | null>(null);
  // "Acknowledge all" asked: the confirmation shows under the heading
  const [asking, setAsking] = useState(false);
  // "Remove" asked for this alert: the question shows in its row
  const [removing, setRemoving] = useState<string | null>(null);
  const mayAct = canAcknowledgeNotice(roles);
  const mayRemove = canRemoveNotices(roles);

  const read = useCallback(
    (keepOrder: boolean) => {
      setError(null);
      loadRecent(sb, tenantId, days)
        .then((r) => {
          setRows(r);
          setOrder((was) => {
            if (!keepOrder) return noticeHistoryOrder(r).map((a) => a.id);
            const known = new Set(was);
            const fresh = noticeHistoryOrder(r.filter((a) => !known.has(a.id))).map((a) => a.id);
            return [...fresh, ...was.filter((id) => r.some((a) => a.id === id))];
          });
        })
        .catch((e: unknown) => setError(message(e)));
    },
    [sb, tenantId, days],
  );
  const reload = useCallback(() => read(false), [read]);
  useEffect(reload, [reload]);

  const run = (what: string, job: () => Promise<string>): void => {
    setBusy(what);
    setNotice(null);
    job()
      .then((text) => {
        setNotice({ ok: true, text });
        read(true);
      })
      .catch((e: unknown) => setNotice({ ok: false, text: message(e) }))
      .finally(() => setBusy(null));
  };

  const who = (id: string | null | undefined): string =>
    !id ? 'someone' : id === userId ? 'you' : (names.get(id) ?? 'a colleague');

  const nowMs = now();
  const byId = new Map((rows ?? []).map((a) => [a.id, a]));
  const ordered = order.flatMap((id) => byId.get(id) ?? []);
  const matching = ordered.filter((a) => a.id === openAlertId || noticeInFilter(fields(a), filter, nowMs));
  const shown = all ? matching : matching.slice(0, FIRST);
  const waiting = (rows ?? []).filter((a) => noticeNeedsAcknowledging(a)).length;
  const bulk = noticesToAcknowledgeAll(rows ?? []);
  const leftOut = waiting - bulk.length;

  return (
    <div className="panel stack" aria-label="Alert history">
      <div className="row row--wrap">
        <h2 className="heading">Alert history</h2>
        <label className="row">
          <span className="label">Show</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value as NoticeHistoryFilter)}>
            {FILTERS.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="row">
          <span className="label">Of the last</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[7, 30, 90].map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
          </select>
        </label>
        <Button
          variant="quiet"
          disabled={!rows || rows.length === 0}
          onClick={() =>
            save(
              `alert-history-${new Date(nowMs).toISOString().slice(0, 10)}.csv`,
              noticeHistoryCsv(
                (rows ?? []).map((a) => ({
                  ...fields(a),
                  title: a.title,
                  body: a.body,
                  occurred_at: a.occurred_at,
                  acknowledged_by_name: a.acknowledged_by ? who(a.acknowledged_by) : null,
                  resolved_by_name: noticeResolvedByTeslyn(a)
                    ? 'Teslyn'
                    : a.resolved_by
                      ? who(a.resolved_by)
                      : null,
                  resolved_note: a.resolved_note ?? null,
                })),
                nowMs,
              ),
            )
          }
        >
          Download CSV
        </Button>
      </div>
      {mayAct && bulk.length > 0 && WAITING_SHOWN.includes(filter) && !asking && (
        <div className="row row--wrap">
          <Button onClick={() => setAsking(true)} disabled={busy !== null}>
            Acknowledge all ({bulk.length})
          </Button>
          {leftOut > 0 && (
            <span className="caption muted">SOS and lost-tracking alerts are acknowledged one by one.</span>
          )}
        </div>
      )}
      {mayAct && asking && (
        <div className="notice stack" role="alertdialog" aria-label="Acknowledge all">
          <p className="body">{acknowledgeAllQuestion(bulk.length, leftOut)}</p>
          <div className="row row--wrap">
            <Button
              variant="primary"
              busy={busy === 'ack:all'}
              disabled={bulk.length === 0}
              onClick={() =>
                run('ack:all', async () => {
                  const n = await acknowledgeAll(
                    sb,
                    tenantId,
                    bulk.map((a) => a.id),
                  );
                  setAsking(false);
                  return `Acknowledged ${n} ${n === 1 ? 'alert' : 'alerts'}.${
                    leftOut > 0
                      ? ' The SOS and lost-tracking alerts still wait for their own Acknowledge.'
                      : ''
                  }`;
                })
              }
            >
              {busy === 'ack:all'
                ? 'Acknowledging…'
                : `Acknowledge ${bulk.length} ${bulk.length === 1 ? 'alert' : 'alerts'}`}
            </Button>
            <Button variant="quiet" disabled={busy === 'ack:all'} onClick={() => setAsking(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {rows && waiting > 0 && WAITING_SHOWN.includes(filter) && (
        <p className="body alert-history__waiting">
          <strong>
            {waiting} {waiting === 1 ? 'alert' : 'alerts'} not acknowledged
          </strong>{' '}
          in the last {days} days, listed first.
          {mayAct ? ' Press “Acknowledge” when you have seen one, so the others know.' : ''}
        </p>
      )}
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : rows === null ? (
        <Loading what="the alert history" />
      ) : shown.length === 0 ? (
        <p className="body muted">
          {rows.length === 0 ? `No alerts in the last ${days} days.` : EMPTY_WORDS[filter](days)}
        </p>
      ) : (
        <ul className="stack alert-history__list">
          {shown.map((a) => {
            const state = noticeState(fields(a), nowMs);
            return (
              <li
                key={a.id}
                className={[
                  'stack alert-item',
                  a.id === openAlertId ? 'panel alert-item--open' : '',
                  noticeNeedsAcknowledging(a) ? 'alert-item--waiting' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-current={a.id === openAlertId ? 'true' : undefined}
              >
                {/* the title, its time and state; "Acknowledge" beside it (under it on a phone) */}
                <div className="alert-item__head">
                  <span className="body alert-item__title">
                    <strong>{a.title}</strong>{' '}
                    <span className="caption muted alert-item__time">· {when(a.occurred_at)}</span>{' '}
                    <span
                      className={
                        state === 'open'
                          ? 'chip chip--fail'
                          : state === 'resolved'
                            ? 'chip chip--pass'
                            : 'chip'
                      }
                    >
                      {NOTICE_STATE_LABEL[state]}
                    </span>
                  </span>
                  {mayAct && noticeNeedsAcknowledging(a) && (
                    <Button
                      className="alert-item__ack"
                      aria-label={`Acknowledge: ${a.title}`}
                      busy={busy === `ack:${a.id}`}
                      onClick={() =>
                        run(`ack:${a.id}`, async () => {
                          await acknowledge(sb, a.id);
                          return `Acknowledged: ${a.title}.`;
                        })
                      }
                    >
                      {busy === `ack:${a.id}` ? 'Acknowledging…' : 'Acknowledge'}
                    </Button>
                  )}
                </div>
                <span className="body">{a.body}</span>
                <span className="row row--wrap">
                  <span className="caption muted">{STATUS_WORDS[a.status](a.recipients)}</span>
                  {a.acknowledged_at ? (
                    <span className="caption">
                      Acknowledged by {who(a.acknowledged_by)} at {when(a.acknowledged_at)}
                      {a.acknowledged_via === 'push'
                        ? ' (from the notice)'
                        : a.acknowledged_via === 'page_all'
                          ? ' (with Acknowledge all)'
                          : ''}
                    </span>
                  ) : mayAct && state !== 'resolved' ? null : (
                    <span className="caption muted">Not acknowledged</span>
                  )}
                  {state === 'snoozed' && a.snoozed_until && (
                    <span className="caption">
                      Snoozed by {who(a.snoozed_by)} until {when(a.snoozed_until)}
                    </span>
                  )}
                  {a.resolved_at && (
                    <span className="caption">
                      {/* R180: an overdue flight Teslyn resolved when its landing was seen */}
                      Resolved by {noticeResolvedByTeslyn(a) ? 'Teslyn' : who(a.resolved_by)} at{' '}
                      {when(a.resolved_at)}
                      {a.resolved_note ? `: ${a.resolved_note}` : ''}
                    </span>
                  )}
                </span>
                {mayAct && (
                  <span className="row row--wrap">
                    {state !== 'resolved' &&
                      canSnoozeNotice(a.kind) &&
                      (state === 'snoozed' ? (
                        <Button
                          variant="quiet"
                          busy={busy === `snooze:${a.id}`}
                          onClick={() =>
                            run(`snooze:${a.id}`, async () => {
                              await snooze(sb, a.id, 0);
                              return `Snooze ended: ${a.title}.`;
                            })
                          }
                        >
                          End snooze
                        </Button>
                      ) : (
                        NOTICE_SNOOZE_CHOICES.map((c) => (
                          <Button
                            key={c.minutes}
                            variant="quiet"
                            busy={busy === `snooze:${a.id}:${c.minutes}`}
                            aria-label={`Snooze ${c.label}: ${a.title}`}
                            onClick={() =>
                              run(`snooze:${a.id}:${c.minutes}`, async () => {
                                await snooze(sb, a.id, c.minutes);
                                return `Snoozed for ${c.label}: no more notices of this kind for the same station or aircraft until then.`;
                              })
                            }
                          >
                            Snooze {c.label}
                          </Button>
                        ))
                      ))}
                    {!canResolveNotice(a.kind) ? null : state === 'resolved' ? (
                      <Button
                        variant="quiet"
                        busy={busy === `reopen:${a.id}`}
                        onClick={() =>
                          run(`reopen:${a.id}`, async () => {
                            await reopen(sb, a.id);
                            return `Reopened: ${a.title}.`;
                          })
                        }
                      >
                        Reopen
                      </Button>
                    ) : resolving === a.id ? (
                      <>
                        <input
                          type="text"
                          aria-label={`Note: ${a.title}`}
                          spellCheck
                          placeholder="What was done (optional)"
                          maxLength={300}
                          value={notes[a.id] ?? ''}
                          onChange={(e) => setNotes({ ...notes, [a.id]: e.target.value })}
                        />
                        <Button
                          busy={busy === `resolve:${a.id}`}
                          onClick={() =>
                            run(`resolve:${a.id}`, async () => {
                              await resolve(sb, a.id, notes[a.id] ?? '');
                              setResolving(null);
                              return `Resolved: ${a.title}.`;
                            })
                          }
                        >
                          Save resolved
                        </Button>
                        <Button variant="quiet" onClick={() => setResolving(null)}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button variant="quiet" onClick={() => setResolving(a.id)}>
                        Resolve
                      </Button>
                    )}
                  </span>
                )}
                {mayRemove &&
                  (removing === a.id ? (
                    <div className="notice stack" role="alertdialog" aria-label={`Remove: ${a.title}`}>
                      <p className="body">{removeNoticeQuestion(a.title)}</p>
                      <div className="row row--wrap">
                        <Button
                          variant="primary"
                          busy={busy === `remove:${a.id}`}
                          onClick={() =>
                            run(`remove:${a.id}`, async () => {
                              await removeAlert(sb, a.id);
                              setRemoving(null);
                              return `Removed from the history: ${a.title}.`;
                            })
                          }
                        >
                          {busy === `remove:${a.id}` ? 'Removing…' : 'Remove alert'}
                        </Button>
                        <Button variant="quiet" onClick={() => setRemoving(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <span className="row row--wrap">
                      <Button
                        variant="quiet"
                        aria-label={`Remove: ${a.title}`}
                        disabled={noticeRemoveProblem(a) !== null || busy !== null}
                        title={noticeRemoveProblem(a) ?? undefined}
                        onClick={() => setRemoving(a.id)}
                      >
                        Remove
                      </Button>
                      {noticeRemoveProblem(a) && (
                        <span className="caption muted">Acknowledge it before removing it.</span>
                      )}
                    </span>
                  ))}
              </li>
            );
          })}
        </ul>
      )}
      {!all && matching.length > FIRST && (
        <div className="row">
          <Button variant="quiet" onClick={() => setAll(true)}>
            Show {matching.length - FIRST} more
          </Button>
        </div>
      )}
    </div>
  );
}
